import moment from 'moment'
import type { Moment } from 'moment'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { TripVehicleType } from '@/app/constants/delegation/tripVehicleType'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'

export const HOMELAND: CountryModel = {
  code: 'pl',
  name: 'Polska',
  nationId: 40,
  id: '1',
}


export const toggleRestOnBaseEvents = ({
  events,
  rest,
  activities,
  country = HOMELAND,
}: {
  events: ReadonlyArray<EventModel>
  rest: { startTime?: any; endTime?: any }
  activities: ReadonlyArray<any>
  country?: CountryModel
}): EventModel[] | null => {
  const restStart = moment(rest.startTime)
  const restEnd = moment(rest.endTime)

  const existingOnRest = events.filter(
    (e) =>
      (e.tripEvent === TripEventType.Begin ||
        e.tripEvent === TripEventType.Finish) &&
      moment(e.date).isSameOrAfter(restStart) &&
      moment(e.date).isSameOrBefore(restEnd),
  )
  const hasBegin = existingOnRest.some(
    (e) => e.tripEvent === TripEventType.Begin,
  )
  const hasFinish = existingOnRest.some(
    (e) => e.tripEvent === TripEventType.Finish,
  )

  const sortByDateWithLp = (next: EventModel[]) =>
    [...next]
      .sort((a, b) => {
        if (a.date < b.date) return -1
        if (a.date > b.date) return 1
        return 0
      })
      .map((e, index) => ({ ...e, lp: index }))

  if (hasBegin && hasFinish) {
    const existingDates = new Set(
      existingOnRest.map((e) => moment(e.date).valueOf()),
    )
    const updated = events.filter(
      (e) =>
        !(
          (e.tripEvent === TripEventType.Begin ||
            e.tripEvent === TripEventType.Finish) &&
          existingDates.has(moment(e.date).valueOf()) &&
          moment(e.date).isSameOrAfter(restStart) &&
          moment(e.date).isSameOrBefore(restEnd)
        ),
    )
    return sortByDateWithLp(updated)
  }

  let nextType: TripEventType
  if (hasBegin) {
    nextType = TripEventType.Finish
  } else if (hasFinish) {
    nextType = TripEventType.Begin
  } else {
    const sortedRelevant = [...events]
      .filter(
        (e) =>
          (e.tripEvent === TripEventType.Begin ||
            e.tripEvent === TripEventType.Finish) &&
          moment(e.date).isBefore(restStart),
      )
      .sort((a, b) =>
        moment(a.date).isBefore(moment(b.date))
          ? -1
          : moment(a.date).isAfter(moment(b.date))
            ? 1
            : 0,
      )
    const lastBeginOrFinish = sortedRelevant[sortedRelevant.length - 1]
    nextType =
      lastBeginOrFinish?.tripEvent === TripEventType.Begin
        ? TripEventType.Finish
        : TripEventType.Begin
  }

  let eventDate: Moment | null = null

  if (nextType === TripEventType.Begin) {
    const candidates = [...activities]
      .filter((a) => a.startTime && moment(a.startTime).isValid())
      .sort((a, b) =>
        moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1,
      )
    const candidate = candidates.find(
      (a) => !moment(a.startTime).isBefore(moment(rest.endTime)),
    )

    eventDate = candidate ? moment(candidate.startTime) : null
  } else {
    const candidates = [...activities]
      .filter((a) => a.endTime && moment(a.endTime).isValid())
      .sort((a, b) => (moment(a.endTime).isBefore(moment(b.endTime)) ? -1 : 1))
      .reverse()
    const candidate = candidates.find(
      (a) => !moment(a.endTime).isAfter(moment(rest.startTime)),
    )

    eventDate = candidate ? moment(candidate.endTime) : null
  }

  if (!eventDate || !eventDate.isValid()) {
    return null
  }

  const newEvent: EventModel = {
    tripEvent: nextType,
    isNew: false,
    temporary: false,
    vehicleType: TripVehicleType.NotSet,
    city: '',
    comments: '',
    date: eventDate,
    isTransit: false,
    registrationNumber: '',
    isChecked: false,
    lp: -1,
    country,
  }

  return sortByDateWithLp([...events, newEvent])
}
